import { Lock } from 'lucide-react'
import { cn } from '@/lib/cn'
import { kickoffLabel } from '@/lib/time'
import {
  gameStatusLabel,
  indexPicks,
  LOCK_TONE_CLASS,
  lockTone,
  OUTCOME_CLASS,
  OUTCOME_LABEL,
  pickOutcome,
  type BoardGame,
  type BoardPick,
} from './pickState'

export type BoardMember = { userId: string; name: string }

function firstName(name: string) {
  return name.trim().split(/\s+/)[0] || name
}

/* Column headings are first names, because a full name turns every column into a
   scroll of its own. Collisions get a last initial rather than being left
   ambiguous -- a pool with two Mikes would otherwise have two identical columns
   and no way to tell whose picks are whose. */
function abbreviate(members: BoardMember[]) {
  const counts = new Map<string, number>()
  for (const member of members) {
    const first = firstName(member.name)
    counts.set(first, (counts.get(first) ?? 0) + 1)
  }

  return new Map(
    members.map((member) => {
      const first = firstName(member.name)
      if ((counts.get(first) ?? 0) === 1) return [member.userId, first]
      const rest = member.name.trim().split(/\s+/).slice(1).join(' ')
      return [member.userId, rest ? `${first} ${rest[0]}.` : first]
    }),
  )
}

/* Games down, members across. The matchup column is sticky so it stays readable
   while the members scroll under it -- with ten members there is no width at
   which both fit on a phone, and wrapping the team codes would cost more than
   the scroll does.

   The sticky cells carry an explicit bg-stock: without a background of their own
   they are transparent, and the scrolling columns show through them. */
export default function PickGrid({
  games,
  picks,
  members,
  tiePolicy,
}: {
  games: BoardGame[]
  picks: BoardPick[]
  members: BoardMember[]
  tiePolicy: string
}) {
  const byGameAndUser = indexPicks(picks)
  const labels = abbreviate(members)

  return (
    <div className="overflow-x-auto">
      <table className="border-collapse text-left">
        <thead>
          <tr>
            <th
              scope="col"
              className="bg-stock border-rule sticky left-0 z-20 border-r border-b px-3 py-2"
            >
              <span className="eyebrow">Game</span>
            </th>
            {members.map((member) => (
              <th
                key={member.userId}
                scope="col"
                title={member.name}
                className="border-rule border-b px-2.5 py-2 whitespace-nowrap"
              >
                <span className="eyebrow">{labels.get(member.userId)}</span>
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {games.map((game) => (
            <tr key={game.id}>
              <th
                scope="row"
                className="bg-stock border-rule sticky left-0 z-10 border-r border-b px-3 py-2.5 align-top font-normal whitespace-nowrap"
              >
                <span className="text-ink text-body block font-mono font-bold">
                  {game.away_team} @ {game.home_team}
                </span>
                <span className="eyebrow mt-1 block">{gameStatusLabel(game, kickoffLabel)}</span>
              </th>

              {members.map((member) => {
                const pick = byGameAndUser.get(`${game.id}:${member.userId}`)

                if (!pick) {
                  return (
                    <td
                      key={member.userId}
                      className="border-rule border-b px-2.5 py-2.5 text-center"
                    >
                      <span className="text-ink-dim text-body font-mono">—</span>
                      <span className="sr-only">no pick</span>
                    </td>
                  )
                }

                const outcome = pickOutcome(game, pick.picked_team, tiePolicy)

                return (
                  <td
                    key={member.userId}
                    className="border-rule border-b px-2.5 py-2.5 text-center"
                  >
                    <span
                      className={cn(
                        'text-body inline-flex items-center gap-1 px-1.5 py-0.5 font-mono',
                        OUTCOME_CLASS[outcome],
                      )}
                    >
                      {pick.picked_team}
                      {pick.is_lock && (
                        <Lock
                          className={cn(
                            'size-3 shrink-0',
                            // A correct pick is a green fill, so its Lock has to
                            // sit on green -- gold on green is unreadable.
                            outcome === 'correct'
                              ? 'text-white'
                              : LOCK_TONE_CLASS[lockTone(outcome)],
                          )}
                          aria-hidden
                        />
                      )}
                    </span>
                    {/* Outcome is carried by colour alone otherwise. */}
                    <span className="sr-only">
                      {OUTCOME_LABEL[outcome]}
                      {pick.is_lock ? ', lock of the week' : ''}
                    </span>
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
